import { ChannelClient } from "@formant/core/client/ChannelClient";
import { defined } from "@formant/core/common/defined";
import { IChannel } from "@formant/core/model/IChannel";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { IChannelStore } from "./IChannelStore";
import { StoreCache } from "./StoreCache";

export class ChannelStore implements IChannelStore {
    private cache = new StoreCache<Uuid, IChannel | false>();
    private listCache = new StoreCache<void, IChannel[]>();

    constructor(
        private channelClient: ChannelClient,
        private authStore: IAuthStore,
    ) {}

    get(id: Uuid): IChannel | false | undefined {
        return this.cache.get(
            id,
            async () =>
                (await this.channelClient.get(
                    await this.authStore.token(),
                    id,
                )) || false,
        );
    }

    list(): IChannel[] | undefined {
        return this.listCache.get(undefined, () => this.fetchList());
    }

    listAsync(): Promise<IChannel[]> {
        return this.listCache.getAsync(undefined, () => this.fetchList());
    }

    async save(channel: IChannel) {
        if (channel.id === undefined) {
            return this.create(channel);
        } else {
            return this.update(channel.id, channel);
        }
    }

    async create(channel: IChannel) {
        const created = await this.channelClient.create(
            await this.authStore.token(),
            channel,
        );
        this.cache.set(defined(created.id), created);
        this.listCache.clear();
        return created;
    }

    async update(id: Uuid, channel: Partial<IChannel>) {
        const updated = await this.channelClient.update(
            await this.authStore.token(),
            id,
            channel,
        );
        this.cache.set(id, updated);
        this.listCache.clear();
        return updated;
    }

    async delete(id: Uuid) {
        this.cache.set(id, false);
        await this.channelClient.delete(await this.authStore.token(), id);
        await this.fetchList();
    }

    private async fetchList(): Promise<IChannel[]> {
        const value = await this.channelClient.list(
            await this.authStore.token(),
        );
        value.forEach(_ => this.cache.set(defined(_.id), _));
        this.listCache.set(undefined, value);
        return value;
    }
}
